/**
 * Deserialization utilities for switchboard data
 * Handles parsing JSON data and creating edges from node data fields
 */
import type { LegacyFormatData } from '../converters/shared/legacyFormatTypes'
import type {
  AutoAttendantNodeData,
  AutoAttendantOption,
  CallDistributionNodeData,
  DayNightNodeData,
  InteractiveWebhookNodeData,
  QueueNodeData,
  ScheduleNodeData,
  SwitchboardEdge,
  SwitchboardFlowData,
  SwitchboardNode,
} from '../converters/shared/reactFlowTypes'
import { NodeType } from '../converters/shared/reactFlowTypes'
import { convertLegacyFormatToReactFlow } from '../converters/toReactFlow/index'

/**
 * Parse JSON data, handling both string and already-parsed formats
 * Also handles Python-style JSON (single quotes, True/False/None)
 */
function parseJsonData(jsonData: string | unknown): unknown {
  let parsed: unknown

  if (typeof jsonData !== 'string') {
    if (!jsonData) {
      return null
    }

    // API configurations are JSON-compatible. Round-tripping already-parsed
    // input prevents edge reconstruction and editor mutations from changing
    // the caller's object or being shared between deserialization calls.
    parsed = JSON.parse(JSON.stringify(jsonData))
  } else {
    // Handle string input (legacy behavior or when backend returns string)
    if (!jsonData || jsonData.trim() === '') {
      return null
    }

    // Parse JSON string
    try {
      // First, try to parse as JSON string
      parsed = JSON.parse(jsonData)
    } catch (parseError) {
      // If parsing fails, check if it's Python-style (single quotes instead of double quotes)
      const isPythonStyle =
        jsonData.trim().startsWith('[') &&
        (jsonData.includes("'") ||
          jsonData.includes('True') ||
          jsonData.includes('False') ||
          jsonData.includes('None'))

      if (isPythonStyle) {
        try {
          // Convert Python-style single quotes to JSON double quotes
          const jsonString = jsonData
            .replace(/True/g, 'true')
            .replace(/False/g, 'false')
            .replace(/None/g, 'null')
            .replace(/'/g, '"')

          parsed = JSON.parse(jsonString)
        } catch (_pythonParseError) {
          // Try double-encoded as fallback
          try {
            const doubleParsed = JSON.parse(JSON.parse(jsonData))
            parsed = doubleParsed
          } catch {
            // Re-throw the original error
            throw parseError
          }
        }
      } else {
        // If it's a string that can't be parsed, try to see if it's double-encoded
        try {
          const doubleParsed = JSON.parse(JSON.parse(jsonData))
          parsed = doubleParsed
        } catch {
          // Re-throw the original error
          throw parseError
        }
      }
    }
  }

  return parsed
}

/**
 * Check if parsed data is in React Flow format
 */
function isReactFlowFormat(parsed: unknown): parsed is SwitchboardFlowData {
  return (
    !!parsed &&
    typeof parsed === 'object' &&
    parsed !== null &&
    !Array.isArray(parsed) &&
    'nodes' in parsed &&
    'edges' in parsed &&
    Array.isArray((parsed as Record<string, unknown>).nodes) &&
    Array.isArray((parsed as Record<string, unknown>).edges)
  )
}

/**
 * Check if parsed data is in legacy format
 */
function isLegacyFormat(
  parsed: unknown,
): parsed is LegacyFormatData | LegacyFormatData[] {
  return (
    !!parsed &&
    (Array.isArray(parsed) ||
      (typeof parsed === 'object' &&
        parsed !== null &&
        !Array.isArray(parsed) &&
        'nodes' in parsed &&
        typeof (parsed as Record<string, unknown>).nodes === 'object'))
  )
}

/**
 * Create edges from auto_attendant node options and fixed handles
 */
function createAutoAttendantEdges(
  node: SwitchboardNode,
  nodeData: AutoAttendantNodeData & Record<string, unknown>,
  nodeMap: Map<string, SwitchboardNode>,
  edges: SwitchboardEdge[],
): void {
  // Handle option connections
  if (nodeData.options && Array.isArray(nodeData.options)) {
    const options = nodeData.options as AutoAttendantOption[]
    const sortedOptions = [...options]
      .filter((opt) => opt.digit && opt.digit.trim() !== '')
      .sort((a, b) => {
        const digitA = parseInt(a.digit || '0', 10) || 0
        const digitB = parseInt(b.digit || '0', 10) || 0
        return digitA - digitB
      })

    const existingOptionEdges = edges
      .filter(
        (e) =>
          e.source === node.id &&
          e.sourceHandle &&
          e.sourceHandle.startsWith('option-'),
      )
      .sort((a, b) => {
        const indexA = parseInt(
          a.sourceHandle?.replace('option-', '') || '0',
          10,
        )
        const indexB = parseInt(
          b.sourceHandle?.replace('option-', '') || '0',
          10,
        )
        return indexA - indexB
      })

    // Match edges to options by position
    sortedOptions.forEach((option, sortedIndex) => {
      const newSourceHandle = `option-${sortedIndex}`
      const existingEdge = existingOptionEdges[sortedIndex]

      if (existingEdge) {
        const edgeId = `${node.id}-${newSourceHandle}-${existingEdge.target}`
        if (existingEdge.sourceHandle !== newSourceHandle) {
          existingEdge.sourceHandle = newSourceHandle
          existingEdge.id = edgeId
        }
      }
    })

    // Remove duplicate edges
    const edgeKeyToEdge = new Map<string, SwitchboardEdge>()
    const edgesToRemove: SwitchboardEdge[] = []
    existingOptionEdges.forEach((edge) => {
      const key = `${edge.source}-${edge.sourceHandle}-${edge.target}`
      if (edgeKeyToEdge.has(key)) {
        edgesToRemove.push(edge)
      } else {
        edgeKeyToEdge.set(key, edge)
      }
    })

    edgesToRemove.forEach((edgeToRemove) => {
      const index = edges.findIndex((e) => e.id === edgeToRemove.id)
      if (index !== -1) {
        edges.splice(index, 1)
      }
    })
  }

  // Create edge for onInvalid
  const existingOnInvalidEdge = edges.find(
    (e) => e.source === node.id && e.sourceHandle === 'onInvalid',
  )

  if (nodeData.onInvalid) {
    const onInvalidTarget = String(nodeData.onInvalid)
    const targetNode = nodeMap.get(onInvalidTarget)

    if (targetNode) {
      const edgeId = `${node.id}-onInvalid-${targetNode.id}`

      if (
        onInvalidTarget === node.id &&
        existingOnInvalidEdge &&
        existingOnInvalidEdge.target !== node.id
      ) {
        existingOnInvalidEdge.target = node.id
        existingOnInvalidEdge.id = edgeId
        existingOnInvalidEdge.sourceHandle = 'onInvalid'
      } else if (existingOnInvalidEdge) {
        if (existingOnInvalidEdge.sourceHandle !== 'onInvalid') {
          existingOnInvalidEdge.sourceHandle = 'onInvalid'
        }
      } else {
        edges.push({
          id: edgeId,
          source: node.id,
          target: targetNode.id,
          sourceHandle: 'onInvalid',
          type: 'default',
        })
      }
    }
  }

  // Create edge for onTimeout
  if (nodeData.onTimeout) {
    const targetNode = nodeMap.get(String(nodeData.onTimeout))
    if (targetNode) {
      const edgeId = `${node.id}-onTimeout-${targetNode.id}`
      if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          source: node.id,
          target: targetNode.id,
          sourceHandle: 'onTimeout',
          type: 'default',
        })
      }
    }
  }
}

/**
 * Create edges from schedule node fixed handles
 */
function createScheduleEdges(
  node: SwitchboardNode,
  nodeData: ScheduleNodeData & Record<string, unknown>,
  nodeMap: Map<string, SwitchboardNode>,
  edges: SwitchboardEdge[],
): void {
  const whenOpenTarget = nodeData.whenOpen || nodeData.when_open
  if (whenOpenTarget) {
    const targetNode = nodeMap.get(String(whenOpenTarget))
    if (targetNode) {
      const edgeId = `${node.id}-whenOpen-${targetNode.id}`
      if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          source: node.id,
          target: targetNode.id,
          sourceHandle: 'whenOpen',
          type: 'default',
        })
      }
    }
  }

  const whenClosedTarget = nodeData.whenClosed || nodeData.when_closed
  if (whenClosedTarget) {
    const targetNode = nodeMap.get(String(whenClosedTarget))
    if (targetNode) {
      const edgeId = `${node.id}-whenClosed-${targetNode.id}`
      if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          source: node.id,
          target: targetNode.id,
          sourceHandle: 'whenClosed',
          type: 'default',
        })
      }
    }
  }

  if (nodeData.holiday) {
    const targetNode = nodeMap.get(String(nodeData.holiday))
    if (targetNode) {
      const edgeId = `${node.id}-holiday-${targetNode.id}`
      if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          source: node.id,
          target: targetNode.id,
          sourceHandle: 'holiday',
          type: 'default',
        })
      }
    }
  }
}

/**
 * Create edges from day_night node fixed handles
 */
function createDayNightEdges(
  node: SwitchboardNode,
  nodeData: DayNightNodeData & Record<string, unknown>,
  nodeMap: Map<string, SwitchboardNode>,
  edges: SwitchboardEdge[],
): void {
  const whenOpenTarget =
    nodeData.whenOpen ||
    nodeData.when_open ||
    nodeData.dayPath ||
    nodeData.day_path

  if (whenOpenTarget) {
    const targetNode = nodeMap.get(String(whenOpenTarget))
    if (targetNode) {
      const edgeId = `${node.id}-whenOpen-${targetNode.id}`
      if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          source: node.id,
          target: targetNode.id,
          sourceHandle: 'whenOpen',
          type: 'default',
        })
      }
    }
  }

  const whenClosedTarget =
    nodeData.whenClosed ||
    nodeData.when_closed ||
    nodeData.nightPath ||
    nodeData.night_path

  if (whenClosedTarget) {
    const targetNode = nodeMap.get(String(whenClosedTarget))
    if (targetNode) {
      const edgeId = `${node.id}-whenClosed-${targetNode.id}`
      if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          source: node.id,
          target: targetNode.id,
          sourceHandle: 'whenClosed',
          type: 'default',
        })
      }
    }
  }
}

/**
 * Create edges from queue node fixed handles
 */
function createQueueEdges(
  node: SwitchboardNode,
  nodeData: QueueNodeData & Record<string, unknown>,
  nodeMap: Map<string, SwitchboardNode>,
  edges: SwitchboardEdge[],
): void {
  if (nodeData.onTimeout && typeof nodeData.onTimeout === 'string') {
    const targetNode = nodeMap.get(String(nodeData.onTimeout))
    if (targetNode) {
      const edgeId = `${node.id}-onTimeout-${targetNode.id}`
      if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          source: node.id,
          target: targetNode.id,
          sourceHandle: 'onTimeout',
          type: 'default',
        })
      }
    }
  }

  const onEscapeTarget = nodeData.onEscape || nodeData.on_escape
  if (onEscapeTarget) {
    const targetNode = nodeMap.get(String(onEscapeTarget))
    if (targetNode) {
      const edgeId = `${node.id}-onEscape-${targetNode.id}`
      if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          source: node.id,
          target: targetNode.id,
          sourceHandle: 'onEscape',
          type: 'default',
        })
      }
    }
  }
}

/**
 * Create edges from transfer_and_rollover node fixed handles
 */
function createTransferAndRolloverEdges(
  node: SwitchboardNode,
  nodeData: Record<string, unknown>,
  nodeMap: Map<string, SwitchboardNode>,
  edges: SwitchboardEdge[],
): void {
  const noAnswerTarget = nodeData.noAnswer || nodeData.no_answer
  if (noAnswerTarget) {
    const targetNode = nodeMap.get(String(noAnswerTarget))
    if (targetNode) {
      const edgeId = `${node.id}-noAnswer-${targetNode.id}`
      if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          source: node.id,
          target: targetNode.id,
          sourceHandle: 'noAnswer',
          type: 'default',
        })
      }
    }
  }

  const busyTarget = nodeData.busy
  if (busyTarget) {
    const targetNode = nodeMap.get(String(busyTarget))
    if (targetNode) {
      const edgeId = `${node.id}-busy-${targetNode.id}`
      if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          source: node.id,
          target: targetNode.id,
          sourceHandle: 'busy',
          type: 'default',
        })
      }
    }
  }
}

/**
 * Create edges from call_distribution node option handles
 */
function createCallDistributionEdges(
  node: SwitchboardNode,
  nodeData: CallDistributionNodeData & Record<string, unknown>,
  nodeMap: Map<string, SwitchboardNode>,
  edges: SwitchboardEdge[],
): void {
  const optionKeys = Object.keys(nodeData).filter((key) =>
    key.startsWith('option-'),
  )

  optionKeys.forEach((key) => {
    const targetValue = nodeData[key]
    if (targetValue) {
      const targetNode = nodeMap.get(String(targetValue))
      if (targetNode) {
        const edgeId = `${node.id}-${key}-${targetNode.id}`
        const existingEdge = edges.find((e) => e.id === edgeId)
        if (existingEdge) {
          if (existingEdge.sourceHandle !== key) {
            existingEdge.sourceHandle = key
          }
        } else {
          const conflictingEdge = edges.find(
            (e) =>
              e.source === node.id &&
              e.target === targetNode.id &&
              (!e.sourceHandle || e.sourceHandle !== key),
          )
          if (conflictingEdge) {
            conflictingEdge.sourceHandle = key
            conflictingEdge.id = edgeId
          } else {
            edges.push({
              id: edgeId,
              source: node.id,
              target: targetNode.id,
              sourceHandle: key,
              type: 'default',
            })
          }
        }
      }
    }
  })
}

/**
 * Create edges from interactive_webhook node option handles and fixed handles
 */
function createInteractiveWebhookEdges(
  node: SwitchboardNode,
  nodeData: InteractiveWebhookNodeData & Record<string, unknown>,
  nodeMap: Map<string, SwitchboardNode>,
  edges: SwitchboardEdge[],
): void {
  // Handle option connections (same logic as call_distribution)
  const optionKeys = Object.keys(nodeData).filter((key) =>
    key.startsWith('option-'),
  )

  optionKeys.forEach((key) => {
    const targetValue = nodeData[key]
    if (targetValue) {
      const targetNode = nodeMap.get(String(targetValue))
      if (targetNode) {
        const edgeId = `${node.id}-${key}-${targetNode.id}`
        const existingEdge = edges.find((e) => e.id === edgeId)
        if (existingEdge) {
          if (existingEdge.sourceHandle !== key) {
            existingEdge.sourceHandle = key
          }
        } else {
          const conflictingEdge = edges.find(
            (e) =>
              e.source === node.id &&
              e.target === targetNode.id &&
              (!e.sourceHandle || e.sourceHandle !== key),
          )
          if (conflictingEdge) {
            conflictingEdge.sourceHandle = key
            conflictingEdge.id = edgeId
          } else {
            edges.push({
              id: edgeId,
              source: node.id,
              target: targetNode.id,
              sourceHandle: key,
              type: 'default',
            })
          }
        }
      }
    }
  })

  // Create edge for onInvalid
  if (nodeData.onInvalid) {
    const targetNode = nodeMap.get(String(nodeData.onInvalid))
    if (targetNode) {
      const edgeId = `${node.id}-onInvalid-${targetNode.id}`
      if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          source: node.id,
          target: targetNode.id,
          sourceHandle: 'onInvalid',
          type: 'default',
        })
      }
    }
  }

  // Create edge for onTimeout
  const onTimeoutTarget = nodeData.onTimeout || nodeData.on_timeout
  if (onTimeoutTarget) {
    const targetNode = nodeMap.get(String(onTimeoutTarget))
    if (targetNode) {
      const edgeId = `${node.id}-onTimeout-${targetNode.id}`
      if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          source: node.id,
          target: targetNode.id,
          sourceHandle: 'onTimeout',
          type: 'default',
        })
      }
    }
  }
}

/**
 * Create edges from custom handle fields in nodes
 * This handles nodes that store connection targets in their data fields
 */
function createEdgesFromNodeData(
  nodes: SwitchboardNode[],
  edges: SwitchboardEdge[],
): void {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]))

  nodes.forEach((node) => {
    if (!node.data) return

    // Auto Attendant
    if (node.type === 'auto_attendant') {
      createAutoAttendantEdges(
        node,
        node.data as AutoAttendantNodeData & Record<string, unknown>,
        nodeMap,
        edges,
      )
    }

    // Schedule
    if (node.type === NodeType.Schedule) {
      createScheduleEdges(
        node,
        node.data as ScheduleNodeData & Record<string, unknown>,
        nodeMap,
        edges,
      )
    }

    // DayNight
    if (node.type === NodeType.DayNight) {
      createDayNightEdges(
        node,
        node.data as DayNightNodeData & Record<string, unknown>,
        nodeMap,
        edges,
      )
    }

    // Queue
    if (node.type === 'queue') {
      createQueueEdges(
        node,
        node.data as QueueNodeData & Record<string, unknown>,
        nodeMap,
        edges,
      )
    }

    // TransferAndRollover
    if (node.type === 'transfer_and_rollover') {
      createTransferAndRolloverEdges(node, node.data, nodeMap, edges)
    }

    // Call Distribution
    if (node.type === 'call_distribution') {
      createCallDistributionEdges(
        node,
        node.data as CallDistributionNodeData & Record<string, unknown>,
        nodeMap,
        edges,
      )
    }

    // Interactive Webhook
    if (node.type === 'interactive_webhook') {
      createInteractiveWebhookEdges(
        node,
        node.data as InteractiveWebhookNodeData & Record<string, unknown>,
        nodeMap,
        edges,
      )
    }
  })
}

/**
 * Serialize switchboard data to JSON string
 *
 * @param flowData - React Flow format data
 * @returns JSON string representation
 */
export function serializeSwitchboardData(
  flowData: SwitchboardFlowData,
): string {
  const dataToSerialize: SwitchboardFlowData = {
    nodes: flowData.nodes || [],
    edges: flowData.edges || [],
  }
  return JSON.stringify(dataToSerialize)
}

/**
 * Deserialize switchboard data from JSON
 * Handles both React Flow format and legacy format
 */
export function deserializeSwitchboardData(
  jsonData: string | unknown,
): SwitchboardFlowData {
  try {
    const parsed = parseJsonData(jsonData)

    if (!parsed) {
      return { nodes: [], edges: [] }
    }

    // Check if it's already in React Flow format
    if (isReactFlowFormat(parsed)) {
      // Create edges from custom handle fields in nodes
      createEdgesFromNodeData(parsed.nodes, parsed.edges)

      return parsed
    }

    // Check if it's the old legacy format
    if (isLegacyFormat(parsed)) {
      return convertLegacyFormatToReactFlow(parsed)
    }

    // Unknown format - return empty flow data
    return { nodes: [], edges: [] }
  } catch (_error) {
    // Silently return empty flow data on parse error
    // Consumers can validate input data themselves if needed
    return { nodes: [], edges: [] }
  }
}
